import React, { useEffect, useRef, useState, useMemo } from 'react';
import { createChart, createSeriesMarkers, IChartApi, ISeriesApi, LineSeries } from 'lightweight-charts';
import type { SeriesMarker, UTCTimestamp } from 'lightweight-charts';

interface StockData {
  stock_code: string;
  stock_name: string;
  price: number;
  change_rate: number;
  volume: number;
  created_at?: string | number; // ISO string 또는 Unix timestamp (백테스트 데이터)
  collected_at?: string | number; // ISO string 또는 Unix timestamp (기존 데이터 호환성)
  timestamp?: string | number; // ISO string 또는 Unix timestamp (기존 데이터 호환성)
}

export type ChartClickPayload = {
  time: number;
  fluRt: number;
  stockCode?: string;
};

export interface StockChartProps {
  data: StockData[];
  loading: boolean;
  error: string | null;
  onRefetch: () => void;
  colors?: string[];
  isBacktest?: boolean;
  usePrice?: boolean; // true일 경우 change_rate 대신 price 사용
  height?: number; // 차트 높이 (기본값: 400px)
  emptyMessage?: string; // 데이터 없을 때 표시할 메시지 (기본: "데이터가 없습니다")
  /** 백테스트: 데이터 없어도 차트 축(빈 캔버스) 유지 */
  keepChartWhenEmpty?: boolean;
  /** isBacktest 축·툴팁 표시 타임존 (기본 UTC). Kiwoom 등: Asia/Seoul */
  chartTimeZone?: string;
  /** 첫 번째 시리즈에 표시할 마커 (급등 시작·종료 등) */
  seriesMarkers?: ChartSeriesMarker[];
  /** 크로스헤어 시각의 종목 현황(등락률 순) 패널 숨김 */
  hideStockStatus?: boolean;
  /** 차트 클릭 시 가장 가까운 데이터 포인트 (급등 정의 입력 등) */
  onChartClick?: (payload: ChartClickPayload) => void;
}

export type ChartSeriesMarker = {
  time: number;
  position: 'aboveBar' | 'belowBar';
  color: string;
  shape: 'arrowUp' | 'arrowDown' | 'circle' | 'square';
  text?: string;
};

function applySeriesMarkers(series: ISeriesApi<'Line'> | undefined, markers: ChartSeriesMarker[]) {
  if (!series) return;
  const sorted = [...markers].sort((a, b) => a.time - b.time);
  createSeriesMarkers(
    series,
    sorted.map(
      (m) =>
        ({
          time: m.time as UTCTimestamp,
          position: m.position,
          color: m.color,
          shape: m.shape,
          text: m.text,
        }) satisfies SeriesMarker<UTCTimestamp>,
    ),
  );
}

function nearestSeriesPoint(
  chartData: Record<string, { time: number; value: number }[]>,
  stockKeys: string[],
  time: number,
  maxDistSec = 3,
) {
  let best: { time: number; value: number; stockKey: string; dist: number } | null = null;
  for (const key of stockKeys) {
    const pts = chartData[key];
    if (!pts?.length) continue;
    for (const p of pts) {
      const dist = Math.abs(p.time - time);
      if (dist <= maxDistSec && (!best || dist < best.dist)) {
        best = { time: p.time, value: p.value, stockKey: key, dist };
      }
    }
  }
  return best;
}

function makeChartClickHandler(
  chartDataRef: React.RefObject<Record<string, { time: number; value: number }[]>>,
  stockKeysRef: React.RefObject<string[]>,
  onChartClickRef: React.RefObject<((payload: ChartClickPayload) => void) | undefined>,
) {
  return (param: { time?: unknown; point?: { x: number; y: number } }) => {
    const cb = onChartClickRef.current;
    if (!cb || param.time == null || !param.point || param.point.x < 0 || param.point.y < 0) return;
    const clickTime = typeof param.time === 'number' ? param.time : Number(param.time);
    if (!Number.isFinite(clickTime)) return;
    const nearest = nearestSeriesPoint(chartDataRef.current ?? {}, stockKeysRef.current ?? [], clickTime);
    if (!nearest) return;
    const stockCode = nearest.stockKey.split('_')[0];
    cb({ time: nearest.time, fluRt: nearest.value, stockCode });
  };
}

const DEFAULT_COLORS = ['#ef4444', '#22c55e', '#3b82f6', '#8b5cf6', '#f59e0b', '#10b981', '#f97316', '#84cc16', '#06b6d4', '#8b5cf6'];

function formatAxisTime(time: number, timeZone?: string): string {
  const date = new Date(Math.floor(time) * 1000);
  const timeString = date.toLocaleTimeString('en-US', {
    timeZone,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
  const fractionalSeconds = (time - Math.floor(time)).toFixed(6).substring(1);
  return `${timeString}${fractionalSeconds}`;
}

function getTimeScaleOptions(backtest: boolean, backtestTz: string) {
  return backtest ? {
    timeVisible: true,
    secondsVisible: true,
    borderColor: '#cccccc',
    rightOffset: 0,
    barSpacing: 0.01,
    lockVisibleTimeRangeOnResize: true,
    rightBarStaysOnScroll: true,
    shiftVisibleRangeOnNewBar: true,
    minBarSpacing: 0.001,
    tickMarkFormatter: (time: number) => formatAxisTime(time, backtestTz),
  } : {
    borderColor: '#cccccc',
    timeVisible: true,
    secondsVisible: true,
    rightOffset: 0,
    barSpacing: 0.01,
    minBarSpacing: 0.001,
    rightBarStaysOnScroll: true,
    shiftVisibleRangeOnNewBar: true,
    tickMarkFormatter: (time: number) => formatAxisTime(time),
  };
}

/** 실시간 차트: 패딩/윈도우용. 최신 포인트가 오른쪽 끝에 고정되고 왼쪽으로 흐름 */
const REALTIME_WINDOW_SEC = 180;
/** 표시할 바(타임스탬프) 수. 1바≈1초 기준으로 3분 ≈ 180 */
const REALTIME_VISIBLE_BARS = 180;

/** 실시간: 첫 포인트를 오른쪽 끝에 보이게 하기 위해 왼쪽에 패딩 포인트 추가 (차트는 데이터 외 범위 extrapolate 불가) */
function addLeftPaddingForRealtime(
  chartData: { [k: string]: { time: number; value: number }[] },
  windowSec: number
): { [k: string]: { time: number; value: number }[] } {
  const allTimes: number[] = [];
  Object.values(chartData).forEach((pts) => pts.forEach((p) => allTimes.push(p.time)));
  const maxTime = allTimes.length > 0 ? Math.max(...allTimes) : Date.now() / 1000;
  const leftEdge = maxTime - windowSec;

  const result: { [k: string]: { time: number; value: number }[] } = {};
  Object.keys(chartData).forEach((key) => {
    const pts = chartData[key];
    if (!pts.length) {
      result[key] = [];
      return;
    }
    const sorted = [...pts].sort((a, b) => a.time - b.time);
    const minTime = sorted[0].time;
    if (minTime > leftEdge) {
      result[key] = [{ time: leftEdge, value: sorted[0].value }, ...sorted];
    } else {
      result[key] = sorted;
    }
  });
  return result;
}

const StockChart: React.FC<StockChartProps> = ({
  data,
  loading,
  error,
  onRefetch,
  colors,
  isBacktest = false,
  usePrice = false,
  height = 400,
  emptyMessage,
  keepChartWhenEmpty = false,
  chartTimeZone,
  seriesMarkers,
  hideStockStatus = false,
  onChartClick,
}) => {
  const backtestTz = chartTimeZone ?? 'UTC';
  // Use stable colors reference
  const chartColors = useMemo(() => colors || DEFAULT_COLORS, [colors]);
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const seriesRef = useRef<ISeriesApi<'Line'>[]>([]);
  const chartColorsRef = useRef<string[]>(chartColors);
  const chartDataRef = useRef<{ [key: string]: { time: number; value: number }[] }>({});
  const stockKeysRef = useRef<string[]>([]);
  /** 실시간 모드: 종목별로 이미 시리즈에 넣은 데이터 (증분 업데이트용) */
  const seriesDataRef = useRef<{ [key: string]: { time: number; value: number }[] }>({});
  const crosshairHandlerRef = useRef<((param: any) => void) | null>(null);
  const clickHandlerRef = useRef<((param: any) => void) | null>(null);
  const onChartClickRef = useRef(onChartClick);
  onChartClickRef.current = onChartClick;
  const seriesMarkersRef = useRef(seriesMarkers);
  seriesMarkersRef.current = seriesMarkers;
  const [stockLabels, setStockLabels] = useState<{ name: string; code: string; value: number }[]>([]);
  const [emptyData, setEmptyData] = useState<boolean>(false);
  const hasFetchedRef = useRef<boolean>(keepChartWhenEmpty && isBacktest);
  const [currentTime, setCurrentTime] = useState<number | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [hasMouseInteracted, setHasMouseInteracted] = useState<boolean>(false);
  const [tooltipData, setTooltipData] = useState<{
    visible: boolean;
    x: number;
    y: number;
    stocks: { name: string; value: number; color: string }[];
  }>({
    visible: false,
    x: 0,
    y: 0,
    stocks: []
  });


  useEffect(() => {
    chartColorsRef.current = chartColors;
  }, [chartColors]);

  // 백테스트 차트 초기화 (완전히 분리된 플로우)
  useEffect(() => {
    // 백테스트가 아니면 이 useEffect는 실행하지 않음
    if (!isBacktest) {
      return;
    }

    console.log('🔧 [백테스트] 차트 초기화 useEffect 실행:', {
      hasContainer: !!chartContainerRef.current,
      hasChart: !!chartRef.current,
      height,
      dataLength: data.length,
      loading,
    });
    
    // 차트가 이미 있으면 재생성하지 않음
    if (chartRef.current) {
      console.log('✅ [백테스트] 차트가 이미 존재함');
      return;
    }

    if (loading) {
      console.log('⏳ [백테스트] 로딩 중 - 대기');
      return;
    }
    if (!data.length && !keepChartWhenEmpty) {
      console.log('⏳ [백테스트] 데이터 없음 - 대기');
      return;
    }
    
    // 컨테이너가 없으면 대기
    if (!chartContainerRef.current) {
      console.log('⏳ [백테스트] 컨테이너 없음 - 대기');
      return;
    }

    const containerRef = chartContainerRef.current;
    if (!document.contains(containerRef)) {
      console.log('⏳ [백테스트] 컨테이너가 DOM에 없음 - 대기');
      return;
    }
    
    // 약간의 지연을 두어 DOM이 완전히 렌더링된 후 실행
    let timer: NodeJS.Timeout | null = null;
    
    timer = setTimeout(() => {
      console.log('🔧 차트 초기화 useEffect 실행 (지연 후):', {
        hasContainer: !!chartContainerRef.current,
        hasChart: !!chartRef.current,
        height,
        isBacktest,
        containerInDOM: chartContainerRef.current ? document.contains(chartContainerRef.current) : false,
        containerWidth: chartContainerRef.current?.clientWidth,
        containerHeight: chartContainerRef.current?.clientHeight,
      });

      if (!chartContainerRef.current) {
        console.warn('❌ 차트 컨테이너 ref가 없음');
        return;
      }

      const containerRef = chartContainerRef.current;

      if (!document.contains(containerRef)) {
        console.warn('❌ 차트 컨테이너가 DOM에 연결되지 않음');
        return;
      }

      if (chartRef.current) {
        console.log('✅ 차트가 이미 존재함 - 재생성하지 않음');
        return;
      }
      
      console.log('📊 차트 생성 시작...');
      const containerWidth = Math.max(containerRef.clientWidth || 400, 400);
      const containerHeight = Math.max(containerRef.clientHeight || height, height);
      
      console.log('📐 컨테이너 크기:', {
        clientWidth: containerRef.clientWidth,
        clientHeight: containerRef.clientHeight,
        calculatedWidth: containerWidth,
        calculatedHeight: containerHeight,
      });

      const chart = createChart(containerRef, {
      width: containerWidth,
      height: containerHeight,
      layout: {
        background: { color: '#ffffff' },
        textColor: '#000000',
        fontSize: 11,
        fontFamily: 'Arial, sans-serif',
      },
      grid: {
        vertLines: { 
          color: '#f8f8f8',
          style: 1, // dotted line
        },
        horzLines: { 
          color: '#f8f8f8',
          style: 1, // dotted line
        },
      },
      rightPriceScale: {
        borderColor: '#cccccc',
        scaleMargins: {
          top: 0.02,
          bottom: 0.01,
        },
      },
      timeScale: getTimeScaleOptions(isBacktest, backtestTz),
      crosshair: {
        mode: 1,
        vertLine: {
          color: '#758696',
          width: 1,
          style: 3,
          labelBackgroundColor: '#232832',
        },
        horzLine: {
          color: '#758696',
          width: 1,
          style: 3,
          labelBackgroundColor: '#232832',
        },
      },
      localization: {
        timeFormatter: (time: number) => formatAxisTime(time, isBacktest ? backtestTz : undefined),
      },
      handleScroll: {
        mouseWheel: true,
        pressedMouseMove: true,
        horzTouchDrag: true,
        vertTouchDrag: true,
      },
      handleScale: {
        axisPressedMouseMove: true,
        mouseWheel: true,
        pinch: false,
        axisDoubleClickReset: false,
      },
    });

      if (!chart) {
        console.error('❌ createChart가 null을 반환함');
        return;
      }
      
      if (!containerRef) {
        console.error('❌ containerRef가 null임');
        return;
      }
      
      if (!document.contains(containerRef)) {
        console.error('❌ containerRef가 DOM에 없음');
        return;
      }
      
      chartRef.current = chart;
      console.log('✅ 차트 생성 성공:', {
        chartWidth: containerRef.clientWidth,
        chartHeight: containerRef.clientHeight,
        chartInstance: !!chart,
        isBacktest,
      });

      // Crosshair 핸들러 설정
      const handleCrosshairMove = (param: any) => {
        const chartData = chartDataRef.current;
        const allStockKeys = stockKeysRef.current;
        const currentColors = chartColorsRef.current;
      if (param.point === undefined || !param.time || param.point.x < 0 || param.point.y < 0) {
        setTooltipData(prev => ({ ...prev, visible: false }));
        return;
      }
      
      setCurrentTime(param.time);
      setHasMouseInteracted(true);
      
      // 해당 시간에 대한 종목 데이터 수집
        const stocksAtTime: { name: string; value: number; color: string }[] = [];
        const timeSpecificStockLabels: { name: string; code: string; value: number }[] = [];
      
      // 모든 종목 확인 (제한 없음)
        const stocksForTooltip = allStockKeys;
        stocksForTooltip.forEach((stockKey, index) => {
        const stockData = chartData[stockKey];
        if (stockData && stockData.length > 0) {
          // 해당 시간에 정확히 일치하는 데이터 포인트 찾기
          const exactData = stockData.find(data => data.time === param.time);
          
          if (exactData) {
            // stockKey에서 종목코드와 종목명 추출 (stock_code_stock_name 형식)
            const parts = stockKey.split('_');
            const stockCode = parts.length > 1 ? parts[0] : '';
            const stockName = parts.length > 1 ? parts.slice(1).join('_') : stockKey;
              stocksAtTime.push({
              name: stockName,
              value: exactData.value,
                color: currentColors[index % currentColors.length]
            });
            
            // 해당 시간 종목 현황용 데이터 추가
              timeSpecificStockLabels.push({
              name: stockName,
              code: stockCode,
              value: exactData.value
            });
          }
        }
        });
      
      // 값 순으로 정렬
        stocksAtTime.sort((a, b) => b.value - a.value);
        timeSpecificStockLabels.sort((a, b) => b.value - a.value);
      
      // 해당 시간의 종목 현황 업데이트
        setStockLabels(timeSpecificStockLabels);
      
      // 툴팁 위치 계산 (차트 영역 내에서 조정)
        let tooltipX = param.point.x + 10;
        let tooltipY = param.point.y - 10;
      
      // 차트 컨테이너 크기 고려하여 위치 조정
        if (containerRef) {
        const containerRect = containerRef.getBoundingClientRect();
        const tooltipWidth = 200; // 예상 툴팁 너비
        const tooltipHeight = Math.min(300, stocksAtTime.length * 20 + 40); // 예상 툴팁 높이
        
        // 오른쪽 경계를 벗어나는 경우 왼쪽으로 이동
        if (tooltipX + tooltipWidth > containerRect.width) {
          tooltipX = param.point.x - tooltipWidth - 10;
        }
        
        // 위쪽 경계를 벗어나는 경우 아래쪽으로 이동
        if (tooltipY < 0) {
          tooltipY = param.point.y + 10;
        }
        
        // 아래쪽 경계를 벗어나는 경우 위쪽으로 이동
        if (tooltipY + tooltipHeight > containerRect.height) {
          tooltipY = containerRect.height - tooltipHeight - 10;
        }
      }
      
        setTooltipData({
        visible: stocksAtTime.length > 0,
        x: tooltipX,
        y: tooltipY,
        stocks: stocksAtTime.slice(0, 10) // 상위 10개만 표시
      });
      };

      crosshairHandlerRef.current = handleCrosshairMove;
      chart.subscribeCrosshairMove(handleCrosshairMove);

      const handleChartClick = makeChartClickHandler(chartDataRef, stockKeysRef, onChartClickRef);
      clickHandlerRef.current = handleChartClick;
      chart.subscribeClick(handleChartClick);
    
    // 마우스가 차트를 벗어날 때 툴팁만 숨기기 (currentTime은 유지)
      const handleMouseLeave = () => {
      setTooltipData(prev => ({ ...prev, visible: false }));
      // setCurrentTime(null); // 마지막 커서 위치 유지
      // setStockLabels(displayStockLabels); // 마지막 커서 위치의 데이터 유지
      };
    
      if (containerRef) {
        containerRef.addEventListener('mouseleave', handleMouseLeave);
      }

    // Resize 핸들러
      const handleResize = () => {
          if (chart && containerRef) {
        try {
            const newWidth = containerRef.clientWidth;
            const newHeight = containerRef.clientHeight;
        chart.applyOptions({
          width: newWidth,
          height: newHeight,
        });
        } catch (error) {
          console.warn('차트 리사이즈 중 오류:', error);
        }
      }
      };

      window.addEventListener('resize', handleResize);

      console.log('✅ [백테스트] 차트 초기화 완료');
    }, 200); // 200ms 지연
    
    // useEffect cleanup 함수
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
      // 차트가 생성되었다면 정리
      if (chartRef.current) {
        try {
          // 이벤트 리스너 정리
          if (crosshairHandlerRef.current) {
            chartRef.current.unsubscribeCrosshairMove(crosshairHandlerRef.current);
          }
          if (clickHandlerRef.current) {
            chartRef.current.unsubscribeClick(clickHandlerRef.current);
            clickHandlerRef.current = null;
          }
          // 차트 제거
          chartRef.current.remove();
          chartRef.current = null;
        } catch (error) {
          console.warn('[백테스트] 차트 cleanup 중 오류:', error);
        }
      }
    };
  }, [isBacktest, backtestTz, data.length, loading, height, keepChartWhenEmpty]);

  // 실시간 차트 초기화 (백테스트가 아닐 때만)
  useEffect(() => {
    // 백테스트면 이 useEffect는 실행하지 않음
    if (isBacktest) {
      return;
    }

    console.log('🔧 [실시간] 차트 초기화 useEffect 실행:', {
      hasContainer: !!chartContainerRef.current,
      hasChart: !!chartRef.current,
      height,
    });
    
    // 차트가 이미 있으면 재생성하지 않음
    if (chartRef.current) {
      console.log('✅ [실시간] 차트가 이미 존재함');
      return;
    }
    
    // 약간의 지연을 두어 DOM이 완전히 렌더링된 후 실행
    let timer: NodeJS.Timeout | null = null;
    
    timer = setTimeout(() => {
      console.log('🔧 [실시간] 차트 초기화 useEffect 실행 (지연 후):', {
        hasContainer: !!chartContainerRef.current,
        hasChart: !!chartRef.current,
        height,
        containerInDOM: chartContainerRef.current ? document.contains(chartContainerRef.current) : false,
        containerWidth: chartContainerRef.current?.clientWidth,
        containerHeight: chartContainerRef.current?.clientHeight,
      });

      if (!chartContainerRef.current) {
        console.warn('❌ [실시간] 차트 컨테이너 ref가 없음');
        return;
      }

      const containerRef = chartContainerRef.current;

      if (!document.contains(containerRef)) {
        console.warn('❌ [실시간] 차트 컨테이너가 DOM에 연결되지 않음');
        return;
      }

      if (chartRef.current) {
        console.log('✅ [실시간] 차트가 이미 존재함 - 재생성하지 않음');
        return;
      }
      
      console.log('📊 [실시간] 차트 생성 시작...');
      const containerWidth = Math.max(containerRef.clientWidth || 400, 400);
      const containerHeight = Math.max(containerRef.clientHeight || height, height);
      
      const chart = createChart(containerRef, {
        width: containerWidth,
        height: containerHeight,
        layout: {
          background: { color: '#ffffff' },
          textColor: '#000000',
          fontSize: 11,
          fontFamily: 'Arial, sans-serif',
        },
        grid: {
          vertLines: { 
            color: '#f8f8f8',
            style: 1,
          },
          horzLines: { 
            color: '#f8f8f8',
            style: 1,
          },
        },
        rightPriceScale: {
          borderColor: '#cccccc',
          scaleMargins: {
            top: 0.02,
            bottom: 0.01,
          },
        },
        timeScale: getTimeScaleOptions(false, backtestTz),
        crosshair: {
          mode: 1,
          vertLine: {
            color: '#758696',
            width: 1,
            style: 3,
            labelBackgroundColor: '#232832',
          },
          horzLine: {
            color: '#758696',
            width: 1,
            style: 3,
            labelBackgroundColor: '#232832',
          },
        },
        localization: {
          timeFormatter: (time: any) => {
            const date = new Date(Math.floor(time) * 1000);
            // 실시간 차트는 로컬 시간대 사용 (timeZone 옵션 제거)
            const timeString = date.toLocaleTimeString('en-US', { 
              hour12: false,
              hour: '2-digit',
              minute: '2-digit',
              second: '2-digit'
            });
            const fractionalSeconds = (time - Math.floor(time)).toFixed(6).substring(1);
            return `${timeString}${fractionalSeconds}`;
          },
        },
        handleScroll: {
          mouseWheel: true,
          pressedMouseMove: true,
          horzTouchDrag: true,
          vertTouchDrag: true,
        },
        handleScale: {
          axisPressedMouseMove: true,
          mouseWheel: true,
          pinch: false,
          axisDoubleClickReset: false,
        },
      });

      if (!chart) {
        console.error('❌ [실시간] createChart가 null을 반환함');
        return;
      }
      
      chartRef.current = chart;
      console.log('✅ [실시간] 차트 생성 성공');

      // Crosshair 핸들러 설정 (백테스트와 동일한 로직)
      const handleCrosshairMove = (param: any) => {
        const chartData = chartDataRef.current;
        const allStockKeys = stockKeysRef.current;
        const currentColors = chartColorsRef.current;
        if (param.point === undefined || !param.time || param.point.x < 0 || param.point.y < 0) {
          setTooltipData(prev => ({ ...prev, visible: false }));
          return;
        }
        
        setCurrentTime(param.time);
        setHasMouseInteracted(true);
        
        const stocksAtTime: { name: string; value: number; color: string }[] = [];
        const timeSpecificStockLabels: { name: string; code: string; value: number }[] = [];
        
        const stocksForTooltip = allStockKeys;
        stocksForTooltip.forEach((stockKey, index) => {
          const stockData = chartData[stockKey];
          if (stockData && stockData.length > 0) {
            const exactData = stockData.find(data => data.time === param.time);
            
            if (exactData) {
              const parts = stockKey.split('_');
              const stockCode = parts.length > 1 ? parts[0] : '';
              const stockName = parts.length > 1 ? parts.slice(1).join('_') : stockKey;
              stocksAtTime.push({
                name: stockName,
                value: exactData.value,
                color: currentColors[index % currentColors.length]
              });
              
              timeSpecificStockLabels.push({
                name: stockName,
                code: stockCode,
                value: exactData.value
              });
            }
          }
        });
        
        stocksAtTime.sort((a, b) => b.value - a.value);
        timeSpecificStockLabels.sort((a, b) => b.value - a.value);
        
        setStockLabels(timeSpecificStockLabels);
        
        let tooltipX = param.point.x + 10;
        let tooltipY = param.point.y - 10;
        
        if (containerRef) {
          const containerRect = containerRef.getBoundingClientRect();
          const tooltipWidth = 200;
          const tooltipHeight = Math.min(300, stocksAtTime.length * 20 + 40);
          
          if (tooltipX + tooltipWidth > containerRect.width) {
            tooltipX = param.point.x - tooltipWidth - 10;
          }
          
          if (tooltipY < 0) {
            tooltipY = param.point.y + 10;
          }
          
          if (tooltipY + tooltipHeight > containerRect.height) {
            tooltipY = containerRect.height - tooltipHeight - 10;
          }
        }
        
        setTooltipData({
          visible: stocksAtTime.length > 0,
          x: tooltipX,
          y: tooltipY,
          stocks: stocksAtTime.slice(0, 10)
        });
      };

      crosshairHandlerRef.current = handleCrosshairMove;
      chart.subscribeCrosshairMove(handleCrosshairMove);

      const handleChartClick = makeChartClickHandler(chartDataRef, stockKeysRef, onChartClickRef);
      clickHandlerRef.current = handleChartClick;
      chart.subscribeClick(handleChartClick);
    
      const handleMouseLeave = () => {
        setTooltipData(prev => ({ ...prev, visible: false }));
      };
    
      if (containerRef) {
        containerRef.addEventListener('mouseleave', handleMouseLeave);
      }

      const handleResize = () => {
        if (chart && containerRef) {
          try {
            const newWidth = containerRef.clientWidth;
            const newHeight = containerRef.clientHeight;
            chart.applyOptions({
              width: newWidth,
              height: newHeight,
            });
          } catch (error) {
            console.warn('[실시간] 차트 리사이즈 중 오류:', error);
          }
        }
      };

      window.addEventListener('resize', handleResize);
      console.log('✅ [실시간] 차트 초기화 완료');
    }, 100);
    
    // useEffect cleanup 함수
    return () => {
      if (timer) {
        clearTimeout(timer);
      }
      if (chartRef.current) {
        try {
          if (crosshairHandlerRef.current) {
            chartRef.current.unsubscribeCrosshairMove(crosshairHandlerRef.current);
          }
          if (clickHandlerRef.current) {
            chartRef.current.unsubscribeClick(clickHandlerRef.current);
            clickHandlerRef.current = null;
          }
          chartRef.current.remove();
          chartRef.current = null;
        } catch (error) {
          console.warn('[실시간] 차트 cleanup 중 오류:', error);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // 실시간 차트는 마운트 시 한 번만 실행 (isBacktest는 조건 체크용이므로 의존성에 포함하지 않음)

  // isBacktest 변경 시 차트 옵션 업데이트
  useEffect(() => {
    const chart = chartRef.current;
    if (!chart) {
      return;
    }
    chart.applyOptions({
      timeScale: getTimeScaleOptions(isBacktest, backtestTz),
      localization: {
        timeFormatter: (time: number) => formatAxisTime(time, isBacktest ? backtestTz : undefined),
      },
    });
  }, [isBacktest, backtestTz]);

  // height 변경 시 차트 크기 업데이트
  useEffect(() => {
    const chart = chartRef.current;
    const containerRef = chartContainerRef.current;
    if (!chart || !containerRef) {
      return;
    }
    try {
      const containerWidth = Math.max(containerRef.clientWidth || 400, 400);
      const containerHeight = Math.max(containerRef.clientHeight || height, height);
      chart.applyOptions({
        width: containerWidth,
        height: containerHeight,
      });
    } catch (error) {
      console.warn('차트 크기 업데이트 중 오류:', error);
    }
  }, [height]);

  // 데이터 처리 및 시리즈 업데이트
  useEffect(() => {
    console.log('🔄 데이터 처리 useEffect 실행:', {
      loading,
      hasChart: !!chartRef.current,
      dataLength: data.length,
      hasContainer: !!chartContainerRef.current,
      isBacktest,
    });
    
    const chart = chartRef.current;
    
    // 차트가 없고 데이터가 있으면 차트를 강제로 생성 시도
    if (!loading && !chart && data.length > 0 && chartContainerRef.current) {
      console.log('⚠️ 차트가 없지만 데이터가 있음. 차트 강제 생성 시도...');
      
      const containerRef = chartContainerRef.current;
      if (document.contains(containerRef)) {
        const containerWidth = Math.max(containerRef.clientWidth || 400, 400);
        const containerHeight = Math.max(containerRef.clientHeight || height, height);
        
        try {
          const newChart = createChart(containerRef, {
            width: containerWidth,
            height: containerHeight,
            layout: {
              background: { color: '#ffffff' },
              textColor: '#000000',
              fontSize: 11,
              fontFamily: 'Arial, sans-serif',
            },
            grid: {
              vertLines: { color: '#f8f8f8', style: 1 },
              horzLines: { color: '#f8f8f8', style: 1 },
            },
            rightPriceScale: {
              borderColor: '#cccccc',
              scaleMargins: { top: 0.02, bottom: 0.01 },
            },
            timeScale: getTimeScaleOptions(isBacktest, backtestTz),
            crosshair: {
              mode: 1,
              vertLine: { color: '#758696', width: 1, style: 3, labelBackgroundColor: '#232832' },
              horzLine: { color: '#758696', width: 1, style: 3, labelBackgroundColor: '#232832' },
            },
            localization: {
              timeFormatter: (time: number) => formatAxisTime(time, isBacktest ? backtestTz : undefined),
            },
            handleScroll: {
              mouseWheel: true,
              pressedMouseMove: true,
              horzTouchDrag: true,
              vertTouchDrag: true,
            },
            handleScale: {
              axisPressedMouseMove: true,
              mouseWheel: true,
              pinch: false,
              axisDoubleClickReset: false,
            },
          });
          
          if (newChart) {
            chartRef.current = newChart;
            console.log('✅ 차트 강제 생성 성공');
            
            // Crosshair 핸들러 설정 (초기화 useEffect와 동일)
            const handleCrosshairMove = (param: any) => {
              const chartData = chartDataRef.current;
              const allStockKeys = stockKeysRef.current;
              const currentColors = chartColorsRef.current;
              if (param.point === undefined || !param.time || param.point.x < 0 || param.point.y < 0) {
                setTooltipData(prev => ({ ...prev, visible: false }));
                return;
              }
              
              setCurrentTime(param.time);
              setHasMouseInteracted(true);
              
              const stocksAtTime: { name: string; value: number; color: string }[] = [];
              const timeSpecificStockLabels: { name: string; code: string; value: number }[] = [];
              
              const stocksForTooltip = allStockKeys;
              stocksForTooltip.forEach((stockKey, index) => {
                const stockData = chartData[stockKey];
                if (stockData && stockData.length > 0) {
                  const exactData = stockData.find(data => data.time === param.time);
                  
                  if (exactData) {
                    const parts = stockKey.split('_');
                    const stockCode = parts.length > 1 ? parts[0] : '';
                    const stockName = parts.length > 1 ? parts.slice(1).join('_') : stockKey;
                    stocksAtTime.push({
                      name: stockName,
                      value: exactData.value,
                      color: currentColors[index % currentColors.length]
                    });
                    
                    timeSpecificStockLabels.push({
                      name: stockName,
                      code: stockCode,
                      value: exactData.value
                    });
                  }
                }
              });
              
              stocksAtTime.sort((a, b) => b.value - a.value);
              timeSpecificStockLabels.sort((a, b) => b.value - a.value);
              
              setStockLabels(timeSpecificStockLabels);
              
              let tooltipX = param.point.x + 10;
              let tooltipY = param.point.y - 10;
              
              if (containerRef) {
                const containerRect = containerRef.getBoundingClientRect();
                const tooltipWidth = 200;
                const tooltipHeight = Math.min(300, stocksAtTime.length * 20 + 40);
                
                if (tooltipX + tooltipWidth > containerRect.width) {
                  tooltipX = param.point.x - tooltipWidth - 10;
                }
                
                if (tooltipY < 0) {
                  tooltipY = param.point.y + 10;
                }
                
                if (tooltipY + tooltipHeight > containerRect.height) {
                  tooltipY = containerRect.height - tooltipHeight - 10;
                }
              }
              
              setTooltipData({
                visible: stocksAtTime.length > 0,
                x: tooltipX,
                y: tooltipY,
                stocks: stocksAtTime.slice(0, 10)
              });
            };
            
            crosshairHandlerRef.current = handleCrosshairMove;
            newChart.subscribeCrosshairMove(handleCrosshairMove);

            const handleChartClick = makeChartClickHandler(chartDataRef, stockKeysRef, onChartClickRef);
            clickHandlerRef.current = handleChartClick;
            newChart.subscribeClick(handleChartClick);
            
            const handleMouseLeave = () => {
              setTooltipData(prev => ({ ...prev, visible: false }));
            };
            
            if (containerRef) {
              containerRef.addEventListener('mouseleave', handleMouseLeave);
            }
            
            const handleResize = () => {
              if (newChart && containerRef) {
                try {
                  const newWidth = containerRef.clientWidth;
                  const newHeight = containerRef.clientHeight;
                  newChart.applyOptions({
                    width: newWidth,
                    height: newHeight,
                  });
                } catch (error) {
                  console.warn('차트 리사이즈 중 오류:', error);
                }
              }
            };
            
            window.addEventListener('resize', handleResize);
          }
        } catch (error) {
          console.error('❌ 차트 강제 생성 실패:', error);
        }
      }
    }
    
    if (loading) {
      hasFetchedRef.current = true;
    }
    // 데이터가 비어있으면 시리즈만 제거 + 빈 상태 표시
    // (hasFetchedRef: loading이 true였던 적이 있어야 함 → 최초 렌더 시 "데이터 없음" 깜빡임 방지)
    if (!loading && (!data || data.length === 0) && hasFetchedRef.current) {
      if (isBacktest && keepChartWhenEmpty) {
        setEmptyData(false);
        setStockLabels([]);
        return;
      }
      setEmptyData(true);
      setStockLabels([]);
      if (chartRef.current) {
        try {
          if (crosshairHandlerRef.current) {
            chartRef.current.unsubscribeCrosshairMove(crosshairHandlerRef.current);
            crosshairHandlerRef.current = null;
          }
          if (clickHandlerRef.current) {
            chartRef.current.unsubscribeClick(clickHandlerRef.current);
            clickHandlerRef.current = null;
          }
          chartRef.current.remove();
        } catch { /* ignore */ }
        chartRef.current = null;
        seriesRef.current = [];
        seriesDataRef.current = {};
        stockKeysRef.current = [];
      }
      return;
    }
    setEmptyData(false);

    if (loading || !chartRef.current) {
      console.log('⏳ 차트 로딩 중이거나 차트가 없음:', { loading, hasChart: !!chartRef.current });
      return;
    }
    
    const currentChart = chartRef.current;

    console.log('📊 차트 데이터 처리 시작:', {
      dataLength: data.length,
      sampleData: data.slice(0, 2),
    });

    const processDataDirect = (data: StockData[]) => {
      if (!data || data.length === 0) {
        console.log('❌ 차트 데이터가 비어있음');
        return { chartData: {}, stockLabels: [] };
      }

      const chartData: { [key: string]: { time: number; value: number }[] } = {};
      const stockLabels: { name: string; code: string; value: number }[] = [];

      // 타임스탬프 파싱 함수 (소수점 이하 6자리 마이크로초 보존)
      const parseTimestampToSeconds = (timestamp: string | number): number => {
        if (typeof timestamp === 'number') {
          // 마이크로초(등락률 백테스트: Redis 키 기준 6자리)
          if (timestamp > 1e15) {
            return timestamp / 1e6;
          }
          // 밀리초
          if (timestamp > 1e12) {
            return timestamp / 1000;
          }
          // 초 단위 (소수점 포함)
          return timestamp;
        }
        
        // 문자열인 경우
        // 형식: "20260127031408.280000" (YYYYMMDDHHmmss.SSSSSS)
        if (/^\d{14}\.\d+$/.test(timestamp)) {
          const year = parseInt(timestamp.substring(0, 4));
          const month = parseInt(timestamp.substring(4, 6)) - 1; // 월은 0부터 시작
          const day = parseInt(timestamp.substring(6, 8));
          const hour = parseInt(timestamp.substring(8, 10));
          const minute = parseInt(timestamp.substring(10, 12));
          const second = parseInt(timestamp.substring(12, 14));
          
          // 소수점 부분 추출 (마이크로초 포함)
          const fractionalPart = parseFloat('0.' + timestamp.substring(15));
          
          // Date.UTC로 초 단위까지 계산 (밀리초는 0으로 설정)
          const date = new Date(Date.UTC(year, month, day, hour, minute, second, 0));
          const secondsSinceEpoch = date.getTime() / 1000;
          
          // 소수점 부분 추가 (마이크로초 보존)
          return secondsSinceEpoch + fractionalPart;
        }
        
        // ISO 형식 (예: "2006-01-02 15:04:05.000000" 또는 "2006-01-02T15:04:05.000000Z")
        if (timestamp.includes('-') || timestamp.includes('T')) {
          const parsed = new Date(timestamp);
          if (!isNaN(parsed.getTime())) {
            return parsed.getTime() / 1000; // 초 단위로 반환
          }
        }
        
        // 파싱 실패
        console.warn('⚠️ 타임스탬프 파싱 실패:', timestamp, typeof timestamp);
        return Date.now() / 1000;
      };

      data.forEach((item) => {
        const stockKey = `${item.stock_code}_${item.stock_name}`;
        let timestamp: number;
        if (item.created_at !== undefined) {
          timestamp = parseTimestampToSeconds(item.created_at);
        } else if (item.collected_at !== undefined) {
          timestamp = parseTimestampToSeconds(item.collected_at);
        } else if (item.timestamp !== undefined) {
          timestamp = parseTimestampToSeconds(item.timestamp);
        } else {
          console.warn('⚠️ 타임스탬프 필드가 없음:', item);
          timestamp = 0;
        }
        
        // 타임스탬프가 0이거나 유효하지 않으면 경고
        if (timestamp === 0 || isNaN(timestamp)) {
          console.warn('⚠️ 유효하지 않은 타임스탬프:', {
            stockKey,
            created_at: item.created_at,
            collected_at: item.collected_at,
            timestamp: item.timestamp,
            parsedTimestamp: timestamp
          });
        }

        if (!chartData[stockKey]) {
          chartData[stockKey] = [];
        }

        chartData[stockKey].push({
          time: timestamp,
          value: usePrice ? item.price : item.change_rate
        });
      });

      Object.keys(chartData).forEach((stockKey) => {
        const stockData = chartData[stockKey];
        if (stockData.length > 0) {
          // 타임스탬프 순으로 정렬 (같은 타임스탬프는 값 순으로 정렬)
          stockData.sort((a, b) => {
            if (a.time === b.time) {
              return b.value - a.value;
            }
            return a.time - b.time;
          });

          // 동일 timestamp 중복 제거 (마지막 값 유지)
          const uniqueDataMap = new Map<number, { time: number; value: number }>();
          for (let i = stockData.length - 1; i >= 0; i--) {
            const item = stockData[i];
            if (!uniqueDataMap.has(item.time)) {
              uniqueDataMap.set(item.time, item);
            }
          }
          const seriesData = Array.from(uniqueDataMap.values()).sort((a, b) => a.time - b.time);
          chartData[stockKey] = seriesData;

          const latestData = seriesData[seriesData.length - 1];
          const parts = stockKey.split('_');
          const stockCode = parts.length > 1 ? parts[0] : '';
          const stockName = parts.length > 1 ? parts.slice(1).join('_') : stockKey;
          stockLabels.push({
            name: stockName,
            code: stockCode,
            value: latestData.value
          });
        }
      });

      stockLabels.sort((a, b) => b.value - a.value);

      return { chartData, stockLabels };
    };

    const { chartData, stockLabels: newStockLabels } = processDataDirect(data);
    
    // 각 종목별 데이터 포인트 수 확인
    const stockDataPointCounts: Record<string, number> = {};
    Object.keys(chartData).forEach(stockKey => {
      stockDataPointCounts[stockKey] = chartData[stockKey].length;
    });
    
    console.log('📊 차트 데이터 처리 완료:', {
      chartDataKeys: Object.keys(chartData).length,
      stockLabelsCount: newStockLabels.length,
      stockDataPointCounts: Object.entries(stockDataPointCounts).slice(0, 5),
    });
    
    setStockLabels(newStockLabels);
    chartDataRef.current = chartData;
    const allStockKeys = Object.keys(chartData);
    const prevStockKeys = stockKeysRef.current;

    if (!currentChart) {
      console.error('❌ 차트 인스턴스가 없어 시리즈를 추가할 수 없음');
      return;
    }

    // 실시간 모드: 기존 시리즈 유지, 새 타임스탬프만 추가 (뷰 끊김 방지)
    if (!isBacktest && prevStockKeys.length > 0) {
      const displayChartData = addLeftPaddingForRealtime(chartData, REALTIME_WINDOW_SEC);
      const newSeriesRefs: ISeriesApi<'Line'>[] = [];
      allStockKeys.forEach((stockKey, index) => {
        const fullData = [...(chartData[stockKey] ?? [])].sort((a, b) => a.time - b.time);
        if (fullData.length === 0) return;

        const prevIdx = prevStockKeys.indexOf(stockKey);
        const existingSeries = prevIdx >= 0 ? seriesRef.current[prevIdx] : undefined;
        const prevData = seriesDataRef.current[stockKey] ?? [];

        if (existingSeries) {
          const lastTime = prevData.length > 0 ? Math.max(...prevData.map((p) => p.time)) : -1;
          const newPoints = fullData.filter((p) => p.time > lastTime).sort((a, b) => a.time - b.time);
          try {
            newPoints.forEach((pt) => existingSeries.update(pt as any));
          } catch (err) {
            console.warn('시리즈 업데이트 오류:', stockKey, err);
          }
          newSeriesRefs.push(existingSeries);
        } else {
          const dataToSet = displayChartData[stockKey] ?? fullData;
          try {
            const seriesInstance = currentChart.addSeries(LineSeries, {
              color: chartColorsRef.current[index % chartColorsRef.current.length],
              lineWidth: 1,
              crosshairMarkerVisible: true,
              crosshairMarkerRadius: 2,
              lastValueVisible: false,
              priceLineVisible: false,
            });
            seriesInstance.setData(dataToSet as any);
            newSeriesRefs.push(seriesInstance);
          } catch (error) {
            console.error('❌ 차트 시리즈 추가 오류:', stockKey, error);
          }
        }
        seriesDataRef.current[stockKey] = fullData;
      });

      prevStockKeys.forEach((prevKey) => {
        if (!allStockKeys.includes(prevKey)) {
          const idx = prevStockKeys.indexOf(prevKey);
          const series = seriesRef.current[idx];
          if (series) {
            try {
              currentChart.removeSeries(series);
            } catch (err) {
              console.warn('시리즈 제거 오류:', prevKey, err);
            }
          }
          delete seriesDataRef.current[prevKey];
        }
      });

      seriesRef.current = newSeriesRefs;
      stockKeysRef.current = allStockKeys;

      // 실시간 증분: setVisibleLogicalRange로 윈도우 고정하지 않고, shiftVisibleRangeOnNewBar에 맡김
      // scrollToPosition(0)만 호출해 최신 데이터가 보이도록
      const applyScroll = () => {
        try {
          currentChart.timeScale().scrollToPosition(0, false);
        } catch (e) {
          // ignore
        }
      };
      requestAnimationFrame(() => requestAnimationFrame(applyScroll));
      applySeriesMarkers(newSeriesRefs[0], seriesMarkersRef.current ?? []);
      return;
    }

    // 백테스트 또는 최초 로드: 시리즈 전부 제거 후 재생성
    seriesRef.current.forEach((series) => {
      try {
        currentChart.removeSeries(series);
      } catch (error) {
        console.warn('차트 시리즈 제거 오류:', error);
      }
    });
    seriesRef.current = [];
    seriesDataRef.current = {};
    stockKeysRef.current = allStockKeys;

    const displayChartData = !isBacktest
      ? addLeftPaddingForRealtime(chartData, REALTIME_WINDOW_SEC)
      : chartData;

    allStockKeys.forEach((stockKey, index) => {
      const stockData = chartData[stockKey];
      if (!stockData || stockData.length === 0) {
        console.warn(`⚠️ ${stockKey}의 데이터가 비어있음`);
        return;
      }

      const sortedData = [...stockData].sort((a, b) => a.time - b.time);
      const dataToSet = (displayChartData[stockKey] ?? sortedData).sort((a, b) => a.time - b.time);

      try {
        const seriesInstance = currentChart.addSeries(LineSeries, {
          color: chartColorsRef.current[index % chartColorsRef.current.length],
          lineWidth: 1,
          crosshairMarkerVisible: true,
          crosshairMarkerRadius: 2,
          lastValueVisible: false,
          priceLineVisible: false,
        });

        seriesInstance.setData(dataToSet as any);
        seriesRef.current.push(seriesInstance);
        seriesDataRef.current[stockKey] = sortedData;
      } catch (error) {
        console.error('❌ 차트 시리즈 추가 오류:', stockKey, error);
      }
    });

    if (currentChart) {
      if (isBacktest) {
        try {
          currentChart.timeScale().fitContent();
        } catch (e) {
          console.warn('fitContent 오류', e);
        }
      } else {
        const uniqueTimes = new Set<number>();
        Object.values(displayChartData).forEach((pts) => pts.forEach((p) => uniqueTimes.add(p.time)));
        const lastBarIdx = uniqueTimes.size - 1;
        const applyRange = () => {
          try {
            if (lastBarIdx >= 0) {
              const from = Math.max(-REALTIME_VISIBLE_BARS, lastBarIdx - REALTIME_VISIBLE_BARS);
              currentChart.timeScale().setVisibleLogicalRange({ from, to: lastBarIdx + 1 });
            }
          } catch (e) {
            console.warn('🔧 [setVisibleLogicalRange:최초로드 오류]', e);
          }
        };
        requestAnimationFrame(() => requestAnimationFrame(applyRange));
      }
    }

    applySeriesMarkers(seriesRef.current[0], seriesMarkersRef.current ?? []);
  }, [data, loading, usePrice, isBacktest, backtestTz, height, keepChartWhenEmpty]);

  useEffect(() => {
    applySeriesMarkers(seriesRef.current[0], seriesMarkers ?? []);
  }, [seriesMarkers]);

  return (
    <div>
      <div style={{ flex: 1 }}>

        {loading && (
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              height: `${height}px`,
              fontSize: '16px',
              color: '#9ca3af',
              backgroundColor: '#fafafa',
              border: '1px dashed #ddd',
              borderRadius: '8px',
            }}>
            Loading...
            </div>
        )}

        {!loading && emptyData && !keepChartWhenEmpty && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            height: `${height}px`,
            fontSize: '15px',
            color: emptyMessage ? '#f59e0b' : '#999',
            backgroundColor: '#fafafa',
            border: '1px dashed #ddd',
            borderRadius: '8px',
          }}>
            {emptyMessage ?? '데이터가 없습니다'}
          </div>
        )}

        {(!loading && (!emptyData || keepChartWhenEmpty)) && (
          <div style={{ position: 'relative' }}>
          <div 
            ref={chartContainerRef} 
            style={{ 
              width: '100%', 
              height: `${height}px`,
                position: 'relative'
              }} 
            />
            
            {/* 툴팁 */}
            {tooltipData.visible && (
                        <div
                          style={{
                            position: 'absolute',
                  left: tooltipData.x + 10,
                  top: tooltipData.y - 10,
                  backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            color: 'white',
                  padding: '8px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            zIndex: 1000,
                pointerEvents: 'none',
                  maxWidth: '200px'
                }}
              >
                <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>
                  {currentTime ? formatAxisTime(currentTime, isBacktest ? backtestTz : undefined) : ''}
                  </div>
                  {tooltipData.stocks.map((stock, index) => (
                    <div key={index} style={{ 
                      display: 'flex', 
                    justifyContent: 'space-between',
                      alignItems: 'center', 
                    marginBottom: '2px'
                    }}>
                    <span style={{ 
                          width: '8px', 
                          height: '8px', 
                          backgroundColor: stock.color,
                          borderRadius: '50%',
                      marginRight: '6px'
                    }} />
                    <span style={{ flex: 1, marginRight: '8px' }}>{stock.name}</span>
                      <span style={{ 
                      color: usePrice ? '#3b82f6' : (stock.value >= 0 ? '#22c55e' : '#ef4444'),
                      fontWeight: 'bold'
                      }}>
                        {usePrice 
                          ? `${stock.value.toLocaleString('ko-KR')}원`
                          : `${stock.value >= 0 ? '+' : ''}${stock.value.toFixed(2)}%`
                        }
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        
        {!hideStockStatus && (
          <div
            style={{
              marginTop: '10px',
              padding: '10px',
              backgroundColor: 'rgba(59, 130, 246, 0.05)',
              borderRadius: '4px',
              border: '1px solid #e0e0e0',
            }}
          >
            <div style={{ fontSize: '12px', fontWeight: 'bold', marginBottom: '8px', color: '#3b82f6' }}>
              {currentTime ?
                `${formatAxisTime(currentTime, isBacktest ? backtestTz : undefined)} 종목 현황 (${usePrice ? '가격 순' : '등락률 순'}) - ${stockLabels.length}개` :
                `종목 현황 (${usePrice ? '가격 순' : '등락률 순'}) - ${stockLabels.length}개`
              }
            </div>
            {stockLabels.length > 0 ? (
              <div style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignContent: 'flex-start',
                alignItems: 'flex-start',
                gap: '6px',
                height: '200px',
                overflowY: 'auto',
              }}>
                {stockLabels.map((label, index) => (
                  <div
                    key={index}
                    style={{
                      backgroundColor: index < 5 ? 'rgba(59, 130, 246, 0.9)' : 'rgba(59, 130, 246, 0.6)',
                      color: 'white',
                      padding: '3px 6px',
                      borderRadius: '3px',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      border: '1px solid rgba(255, 255, 255, 0.3)',
                    }}
                  >
                    {index + 1}. {label.name} ({label.code}): {usePrice ? `${label.value.toLocaleString('ko-KR')}원` : `${label.value >= 0 ? '+' : ''}${label.value.toFixed(2)}%`}
                  </div>
                ))}
              </div>
            ) : (
              <div style={{
                height: '200px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '12px',
                color: '#9ca3af',
                fontStyle: 'italic',
              }}>
                No data to show
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default StockChart;